import type {
    AccountInfo,
    IPublicClientApplication,
} from "@azure/msal-browser";
import { scopes } from "../authentication/PublicClientApplication";
import { API_BASE_URL } from "../constants";
import type { IDataItem } from "../models/IDataItem";
import type { IUserPermissionItem } from "../models/IUserPermissionItem";

export class DataService {
  private instance: IPublicClientApplication;
  private account: AccountInfo;

  constructor(instance: IPublicClientApplication, account: AccountInfo) {
    this.instance = instance;
    this.account = account;
  }

  async fetchWithAuth(url: string, options: RequestInit = {}) {
    const response = await this.instance.acquireTokenSilent({
      scopes,
      account: this.account,
    });
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${response.accessToken}`,
    };
    return fetch(url, { ...options, headers, signal: options.signal });
  }

  async getAppRoles(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/general/permission/rollen?appId=4`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async deleteUserRole(itemId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/general/permission/delete?itemId=${itemId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return true;
  }

  async addUserRole(item: IUserPermissionItem): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/general/permission/add`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<boolean | string>;
    }
  }

  async getRolesAppPermissions(
    rollenId: string,
  ): Promise<IUserPermissionItem[]> {
    const url = `${API_BASE_URL}/fhelix/general/permission/rolesapppermissions?appId=4&rollenId=${rollenId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IUserPermissionItem[]>;
  }

  async getUserAppPermissions(
    userUpn: string,
    appId: string,
  ): Promise<IUserPermissionItem[]> {
    console.log(
      `DataService.getUserAppPermissions called with userUpn=${userUpn}, appId=${appId}, account=${this.account.username}, ${API_BASE_URL}`,
    );
    const url = `${API_BASE_URL}/fhelix/general/permission/userpermissions?userUpn=${userUpn}&appId=${appId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IUserPermissionItem[]>;
  }

  async getMitarbeiterSearchByUpn(upn: string): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/personal/mitarbeitersearchbyupn?search=${upn}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<IDataItem[]>;
    }
  }
}
